import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const CartContext = createContext(null);

const GUEST_CART_KEY = "gaina_guest_cart"; // array of { book_id, quantity }

const loadGuestCart = () => {
  try {
    return JSON.parse(localStorage.getItem(GUEST_CART_KEY) || "[]");
  } catch { return []; }
};
const saveGuestCart = (items) => {
  try { localStorage.setItem(GUEST_CART_KEY, JSON.stringify(items)); } catch { /* ignore */ }
};

export function CartProvider({ children }) {
  const { user, loading: authLoading } = useAuth();
  const [cart, setCart] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(false);

  const hydrateGuestCart = async () => {
    const raw = loadGuestCart();
    if (!raw.length) { setCart({ items: [], total: 0 }); return; }
    const items = [];
    let total = 0;
    for (const g of raw) {
      try {
        const { data: book } = await api.get(`/books/${g.book_id}`);
        const subtotal = (book.price || 0) * g.quantity;
        total += subtotal;
        items.push({ book, quantity: g.quantity, subtotal });
      } catch { /* skip missing */ }
    }
    setCart({ items, total: Math.round(total * 100) / 100 });
  };

  const refresh = useCallback(async () => {
    if (authLoading) return; // wait for auth to settle
    setLoading(true);
    try {
      if (user) {
        const { data } = await api.get("/cart");
        setCart(data);
      } else {
        await hydrateGuestCart();
      }
    } catch (e) {
      setCart({ items: [], total: 0 });
    } finally {
      setLoading(false);
    }
  }, [user, authLoading]);

  useEffect(() => { refresh(); }, [refresh]);

  const addToCart = async (book_id, quantity = 1) => {
    // Wait for auth to settle before deciding auth vs guest branch
    if (authLoading) {
      toast.info("Loading…");
      return { ok: false };
    }
    if (user) {
      try {
        await api.post("/cart/add", { book_id, quantity });
        await refresh();
        toast.success("Added to cart");
        return { ok: true };
      } catch (e) { toast.error(apiError(e)); return { ok: false }; }
    }
    // Guest branch
    try {
      const { data: book } = await api.get(`/books/${book_id}`);
      if (book.out_of_stock_override || (book.stock || 0) <= 0) {
        toast.error("This book is out of stock");
        return { ok: false };
      }
      const items = loadGuestCart();
      const found = items.find((i) => i.book_id === book_id);
      const desired = (found?.quantity || 0) + quantity;
      if (desired > (book.stock || 0)) {
        toast.error(`Only ${book.stock} unit(s) available`);
        return { ok: false };
      }
      if (found) found.quantity = desired;
      else items.push({ book_id, quantity });
      saveGuestCart(items);
      await refresh();
      toast.success("Added to cart");
      return { ok: true };
    } catch (e) { toast.error(apiError(e)); return { ok: false }; }
  };

  const updateQty = async (book_id, quantity) => {
    if (user) {
      try { await api.post("/cart/update", { book_id, quantity }); await refresh(); }
      catch (e) { toast.error(apiError(e)); }
      return;
    }
    let items = loadGuestCart();
    if (quantity <= 0) items = items.filter((i) => i.book_id !== book_id);
    else {
      const it = items.find((i) => i.book_id === book_id);
      if (it) it.quantity = quantity; else items.push({ book_id, quantity });
    }
    saveGuestCart(items);
    await refresh();
  };

  const removeItem = async (book_id) => {
    if (user) {
      try { await api.post("/cart/remove", { book_id, quantity: 1 }); await refresh(); }
      catch (e) { toast.error(apiError(e)); }
      return;
    }
    saveGuestCart(loadGuestCart().filter((i) => i.book_id !== book_id));
    await refresh();
  };

  const clear = async () => {
    if (user) {
      try { await api.post("/cart/clear"); await refresh(); }
      catch (e) { console.error("cart clear failed", e); }
      return;
    }
    saveGuestCart([]);
    await refresh();
  };

  const count = cart.items.reduce((n, i) => n + (i.quantity || 0), 0);

  // Expose raw guest cart items (for guest-checkout call)
  const getGuestItems = () => loadGuestCart();

  return (
    <CartContext.Provider value={{ cart, count, loading, addToCart, updateQty, removeItem, clear, refresh, getGuestItems }}>
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  return useContext(CartContext);
}
